import * as Immutable from 'immutable';
import UserImageListStore from './UserImageListStore';
import UserProductDesignListStore from './UserProductDesignListStore';
import { IUserAddressStore, UserAddressStore } from './UserAddressStore';
import { IUserCreditCardStore, UserCreditCardStore } from './UserCreditCardStore';
import UserCartStore, { IUserCartStore, CartStatus } from './UserCartStore';
import { IUserTaxNumberStore, UserTaxNumberStore } from './UserTaxNumberStore';
import UserPrefsStore from './UserPrefsStore';
import UserStorePresetStore from './Store/UserStorePresetStore';
import UserStorePageStore from './Store/UserStorePageStore';
import UserStoreStore from './Store/UserStoreStore';
import UserAddressListStore from './UserAddressListStore';
import UserCreditCardListStore from './UserCreditCardListStore';
import { CollectionStore } from './CollectionStore';
import OrderListStore from './OrderListStore';
import { BrandingOptionStore } from './BrandingOptionStore';
import UserRewardStore from './UserRewardStore';
import PhotoLabCreationListStore from './PhotoLabCreationListStore';
import UserGoalStore from './UserGoalStore';
import StoreProductListStore from './Store/StoreProductListStore';

export interface UserDataPayload {
	id: number
	loggedIn: boolean
	validated: boolean
	email: string
	name: string
	phone: string
	newsletter_news: boolean
	newsletter_offers: boolean
	favoriteViewProducts: number[]
}

export interface IUserDataStore {
	fetched: boolean
	id: number
	loggedIn: boolean
	email: string
	name: string
	phone: string
	validated: boolean
	newsletter_news: boolean
	newsletter_offers: boolean
	favoriteViewProducts: Immutable.OrderedSet<number>
	addresses: UserAddressListStore
	creditCards: UserCreditCardListStore
	orders: OrderListStore,
	goals: Immutable.OrderedMap<string, UserGoalStore>
	rewards: Immutable.OrderedMap<string, UserRewardStore>
	images: UserImageListStore
	productDesigns: UserProductDesignListStore
	collections: Immutable.OrderedMap<string, CollectionStore>
	brandingOptions: Immutable.OrderedMap<string, BrandingOptionStore>
	photoLabCreations: PhotoLabCreationListStore
	prefs: UserPrefsStore
	taxNumbers: Immutable.OrderedMap<number, UserTaxNumberStore>
	storePresets: Immutable.OrderedMap<number, UserStorePresetStore>
	storePages: Immutable.OrderedMap<string, UserStorePageStore>
	stores: Immutable.OrderedMap<string, UserStoreStore>
}

const defaultStore = Immutable.Record<IUserDataStore>({
	fetched: false,
	id: 0,
	loggedIn: false,
	email: "",
	name: '',
	phone: '',
	validated: false,
	newsletter_news: false,
	newsletter_offers: false,
	favoriteViewProducts: Immutable.OrderedSet<number>(),
	addresses: new UserAddressListStore(),
	creditCards: new UserCreditCardListStore(),
	orders: new OrderListStore(),
	goals: Immutable.OrderedMap<string, UserGoalStore>(),
	rewards: Immutable.OrderedMap<string, UserRewardStore>(),
	images: new UserImageListStore(),
	productDesigns: new UserProductDesignListStore(),
	photoLabCreations: new PhotoLabCreationListStore(),
	collections: Immutable.OrderedMap<string, CollectionStore>(),
	brandingOptions: Immutable.OrderedMap<string, BrandingOptionStore>(),
	prefs: new UserPrefsStore(),
	taxNumbers: Immutable.OrderedMap<number, UserTaxNumberStore>(),
	storePresets: Immutable.OrderedMap<number, UserStorePresetStore>(),
	storePages: Immutable.OrderedMap<string, UserStorePageStore>(),
	stores: Immutable.OrderedMap<string, UserStoreStore>(),
});

export default class UserDataStore extends defaultStore implements IUserDataStore {
	constructor(storeObject: unknown = {}) {
		super(Immutable.fromJS(storeObject, (key, value, path) => {
			if (path[0] === 'favoriteViewProducts') {
				return Immutable.OrderedSet<number>(value.toArray().map(Number));
			}

			if (path[0] === 'rewards') {
				if (path.length === 1) {
					return value.toOrderedMap();
				}

				if (path.length === 2) {
					return new UserRewardStore(value.toObject());
				}
			}

			return Immutable.isKeyed(value) ? value.toOrderedMap() : value.toList();
		}) as Partial<IUserDataStore>);
	}
}
